import { useRef, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { uploadImage } from "../../../js/api/member-profile/member-profile";
import { SharedIcon } from "../shared/shared-icon";
import { sizeRuleMessage, tooLargeMessage } from "../../../js/upload-limits";
import { postFocalPoint } from "../../../js/api/member-media/focal-point";
import { CENTRE, FocalPointPicker, toCssPosition, toPoint } from "../shared/focal-point-picker";
import { Dialog, DialogContent, DialogTitle } from "../shared/modal";

/**
 * Adds images to a member profile item and shows what has been added.
 *
 * Handles both shapes the zones need: a single image (a project's before, after or thumbnail; a
 * video's poster) and a set (a story's images, up to the configured maximum).
 *
 * The designs draw this as a tile grid — 180px filled tiles carrying a circular bin button, and a
 * long-dashed tile at the end that is the drop target, counting down how many are left. The dashed
 * tile IS the FileUploader, so dragging onto it works as well as clicking it.
 *
 * ALT TEXT IS COLLECTED BEFORE THE UPLOAD, not after. All three tickets make it mandatory, the
 * server refuses without it, and asking afterwards would mean telling a member their file was
 * fine and then rejecting it — so the field gates the picker instead. The designs do not draw an
 * alt text box at all; it is kept because the requirement is real and losing it would fail the
 * ticket's own accessibility criteria.
 *
 * Every limit comes from props, which come from appsettings by way of the view. Nothing here may
 * carry a number: the client has to state and enforce exactly what the server enforces.
 *
 * FRAMING. Each image keeps its whole picture; what the member chooses is the focal point — the
 * part that stays in view when a tile, card or slide cuts it to another shape. It is offered
 * straight after an upload (on the file the server stored) and on every tile afterwards via
 * "Adjust framing", in a dialog, and saved at once against the media item, independently of the item's own
 * Save. The source prop names the zone, which is what the server's ownership check keys on.
 */
export function ImageField({
  label,
  hint,
  images,
  onChange,
  maxImages = 1,
  maxFileSizeBytes,
  testId,
  error,
  copy,
  source,
  previews,
}) {
  const [altText, setAltText] = useState("");
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [altTextFlagged, setAltTextFlagged] = useState(false);
  const altInputRef = useRef(null);

  // The image whose framing is open, and the point being chosen for it (not saved until "Save").
  const [framingKey, setFramingKey] = useState(null);
  const [draftPoint, setDraftPoint] = useState(CENTRE);
  const [framingError, setFramingError] = useState(null);
  const [isSavingFraming, setIsSavingFraming] = useState(false);
  const fieldRef = useRef(null);
  const framingImage = framingKey ? images.find((image) => image.key === framingKey) : null;

  const c = copy ?? {};
  const isMultiple = maxImages > 1;
  const remaining = maxImages - images.length;
  const isFull = remaining <= 0;
  const needsAltText = altText.trim().length === 0;
  // Derived rather than cleared on typing: the moment there is a description the flag has nothing
  // to say, and it comes back if the member empties the box and tries the tile again.
  const showAltTextError = altTextFlagged && needsAltText;
  const altHintId = `${testId}-alt-hint`;

  /**
   * The tile is the obvious thing to click, and while there is no description it does nothing.
   * Rather than leave the member wondering, send them to the box that unblocks it and mark it.
   *
   * Runs in the CAPTURE phase on the tile's wrapper: react-drag-drop-files' own label handler calls
   * stopPropagation, so a bubbling listener would never hear the click.
   */
  const promptForAltText = (event) => {
    if (!needsAltText || isUploading) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    setAltTextFlagged(true);

    const input = altInputRef.current;

    input?.focus({ preventScroll: true });
    input?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  const handleSelected = async (file) => {
    setUploadError(null);

    // The size gate runs here as well as on the server so a member on a slow connection is told
    // immediately rather than after uploading the whole file.
    if (file.size > maxFileSizeBytes) {
      // Word for word the server's own message, from the shared helper, so the client gate and
      // the server rejection cannot say different things about the same limit.
      setUploadError(tooLargeMessage(file.size, maxFileSizeBytes));
      return;
    }

    setIsUploading(true);

    const result = await uploadImage(file, altText);

    setIsUploading(false);

    if (!result.isSuccess) {
      setUploadError(result.message);
      return;
    }

    // The server answers with a key, not a URL — the item has not been saved yet, so the media
    // has no place on the page to be addressed from. The file is right here in the browser
    // though, so the tile shows THAT rather than a coloured block with a description in it. It is
    // local-only: `previewUrl` never goes back over the wire, and toRequest sends bare keys.
    const previewUrl = URL.createObjectURL(file);
    const uploaded = { key: result.mediaKey, url: null, previewUrl, sourceUrl: result.url, altText };

    onChange(isMultiple ? [...images, uploaded] : [uploaded]);

    setAltText("");
    setAltTextFlagged(false);

    // Straight on to framing: this is the moment the member is looking at the picture anyway.
    openFraming(uploaded);
  };

  const openFraming = (image) => {
    setFramingError(null);
    setDraftPoint(toPoint(image.focalLeft, image.focalTop));
    setFramingKey(image.key);
  };

  // Focus goes back to the tile's own "Adjust framing" button, or to the alt text box when the
  // dialog opened straight after an upload (there was no trigger for Radix to return to).
  const closeFraming = () => {
    const key = framingKey;

    setFramingKey(null);
    setFramingError(null);

    requestAnimationFrame(() => {
      const button = fieldRef.current?.querySelector(`[data-framing-key="${CSS.escape(String(key))}"]`);

      (button ?? altInputRef.current)?.focus({ preventScroll: true });
    });
  };

  const saveFraming = async () => {
    if (!framingImage) {
      return;
    }

    setIsSavingFraming(true);
    setFramingError(null);

    const result = await postFocalPoint({
      source,
      mediaKey: framingImage.key,
      left: draftPoint.left,
      top: draftPoint.top,
    });

    setIsSavingFraming(false);

    if (!result.isSuccess) {
      setFramingError(result.message);
      return;
    }

    onChange(
      images.map((image) =>
        image.key === framingImage.key ? { ...image, focalLeft: result.left, focalTop: result.top } : image
      )
    );

    closeFraming();
  };

  const handleRemove = (key) => {
    // Releasing it here rather than on unmount: the tile is gone the moment this runs, and a
    // blob the browser is still holding for a picture nobody can see is a leak for the life of
    // the page.
    const going = images.find((image) => image.key === key);

    if (going?.previewUrl) {
      URL.revokeObjectURL(going.previewUrl);
    }

    if (framingKey === key) {
      setFramingKey(null);
    }

    onChange(images.filter((image) => image.key !== key));
  };

  return (
    <fieldset className="member-profile-editor__field" data-testid={testId} ref={fieldRef}>
      <legend className="member-profile-editor__field-label">{label}</legend>

      {hint ? <p className="member-profile-editor__hint">{hint}</p> : null}

      {/*
        ALT TEXT COMES FIRST, above the tiles.

        It used to sit underneath them, which made the field look broken: the dashed tile is the
        obvious thing to click, the picker is disabled until there is a description, and the box
        that unblocks it was below the thing that would not respond. Nothing said why.
      */}
      {!isFull ? (
        <div className="member-profile-editor__alt">
          <label className="member-profile-editor__alt-label" htmlFor={`${testId}-alt`}>
            {c.altTextLabel}
          </label>

          <input
            id={`${testId}-alt`}
            ref={altInputRef}
            type="text"
            className="member-profile-editor__input"
            data-testid="image-alt-text"
            value={altText}
            onChange={(event) => setAltText(event.target.value)}
            aria-invalid={showAltTextError || undefined}
            aria-describedby={needsAltText ? altHintId : undefined}
          />

          {/* Keyed so the flagged version mounts fresh: a role="alert" added to an element that is
              already on the page is not reliably announced. */}
          {showAltTextError ? (
            <p
              key="alt-error"
              id={altHintId}
              className="member-profile-editor__error"
              role="alert"
              data-testid="image-alt-text-error"
            >
              {c.altTextRequired}
            </p>
          ) : needsAltText ? (
            <p key="alt-hint" id={altHintId} className="member-profile-editor__hint">
              {c.altTextRequired}
            </p>
          ) : null}
        </div>
      ) : null}

      <ul className="member-profile-editor__images">
        {images.map((image) => (
          <li key={image.key} className="member-profile-editor__image">
            {/* `url` is the saved item's media URL; `previewUrl` is the local blob for something
                uploaded in this sitting. The description is the last resort — a saved image whose
                media has since gone. */}
            {image.url || image.previewUrl ? (
              <img
                src={image.url ?? image.previewUrl}
                alt={image.altText ?? ""}
                loading="lazy"
                // The tile is a crop too, so it shows the framing the member chose.
                style={{ objectPosition: toCssPosition(toPoint(image.focalLeft, image.focalTop)) }}
                data-testid="image-tile"
              />
            ) : (
              <span className="member-profile-editor__image-pending">{image.altText}</span>
            )}

            {source && (image.url || image.sourceUrl || image.previewUrl) ? (
              <button
                type="button"
                className="member-profile-editor__image-framing"
                data-testid="image-adjust-framing"
                data-framing-key={image.key}
                onClick={() => openFraming(image)}
                aria-label={template(c.adjustFramingFor ?? "Adjust framing of {0}", image.altText || c.imageFallbackName)}
                aria-haspopup="dialog"
              >
                {c.adjustFraming ?? "Adjust framing"}
              </button>
            ) : null}

            <button
              type="button"
              className="member-profile-editor__image-remove"
              data-testid="image-remove"
              onClick={() => handleRemove(image.key)}
              aria-label={template(c.removeImage, image.altText || c.imageFallbackName)}
            >
              <SharedIcon iconName="#sprite-bin" aria-hidden="true" />
            </button>
          </li>
        ))}

        {!isFull ? (
          <li
            className="member-profile-editor__image member-profile-editor__image--add"
            onClickCapture={promptForAltText}
            onDropCapture={promptForAltText}
            // Without this a file dragged onto the disabled tile is opened by the browser instead
            // of producing a drop event, and the page navigates away from the member's edits.
            onDragOverCapture={needsAltText ? (event) => event.preventDefault() : undefined}
          >
            {/* Disabled until there is alt text: the server refuses without it, so letting the
                member pick a file first would only waste the upload. Clicking or dropping on it
                meanwhile moves focus to the description instead (promptForAltText). */}
            <FileUploader
              handleChange={handleSelected}
              name="image"
              types={["JPG", "JPEG", "PNG", "WEBP"]}
              disabled={needsAltText || isUploading}
              classes="member-profile-editor__dropzone"
            >
              <span className="member-profile-editor__dropzone-inner" data-testid="image-dropzone">
                <SharedIcon
                  className="member-profile-editor__dropzone-icon"
                  iconName="#sprite-image-add"
                  aria-hidden="true"
                />

                <span className="member-profile-editor__dropzone-label">
                  {isUploading
                    ? c.uploading
                    : needsAltText
                      ? c.addPhotoBlocked
                      : isMultiple
                        ? c.addPhotos
                        : c.addPhoto}
                </span>

                {!isUploading && isMultiple ? (
                  <span className="member-profile-editor__dropzone-note">
                    {template(c.imagesRemaining, remaining)}
                  </span>
                ) : null}
              </span>
            </FileUploader>
          </li>
        ) : null}
      </ul>

      {/* The same dialog the cover and collection images use. An inline panel under the tiles
          pushed the rest of the form down and read as part of the field rather than a step of its
          own. Escape, the overlay and the ✕ all cancel without saving. */}
      {framingImage ? (
        <Dialog open onOpenChange={(open) => !open && !isSavingFraming && closeFraming()}>
          <DialogContent aria-describedby={undefined} onCloseAutoFocus={(event) => event.preventDefault()}>
            <div className="hero-image__header">
              <DialogTitle className="hero-image__title">{c.framingTitle ?? "Adjust framing"}</DialogTitle>
            </div>

            <div className="hero-image__main" data-testid="image-framing">
              <FocalPointPicker
                src={framingImage.url ?? framingImage.sourceUrl ?? framingImage.previewUrl}
                alt={framingImage.altText ?? ""}
                value={draftPoint}
                onChange={setDraftPoint}
                previews={previews}
                disabled={isSavingFraming}
                testId="image-focal-point"
              />

              {framingError ? (
                <p className="member-profile-editor__error" role="alert" data-testid="image-framing-error">
                  {framingError}
                </p>
              ) : null}
            </div>

            <div className="hero-image__footer">
              <div className="hero-image__buttons-wrapper">
                <button
                  type="button"
                  className="my-profile__submit-button"
                  onClick={closeFraming}
                  disabled={isSavingFraming}
                >
                  {c.cancelFraming ?? "Cancel"}
                </button>

                <button
                  type="button"
                  className="my-profile__submit-button my-profile__submit-button--dark"
                  onClick={saveFraming}
                  disabled={isSavingFraming}
                  data-testid="image-framing-save"
                >
                  {isSavingFraming ? (c.savingFraming ?? "Saving…") : (c.saveFraming ?? "Save framing")}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}

      {/* Under the tiles rather than in one: the tile is a 180px square, and the design gives it
          only the icon, the label and the count. */}
      {!isFull ? (
        <p className="member-profile-editor__size-note">{sizeRuleMessage(maxFileSizeBytes)}</p>
      ) : null}

      {/* The server's own wording where there is any: it names the real limit or reason and is
          already member-facing. */}
      {uploadError ? (
        <p className="member-profile-editor__error" role="alert" data-testid="image-upload-error">
          {uploadError}
        </p>
      ) : null}

      {error ? (
        <p className="member-profile-editor__error" role="alert" data-testid="field-error">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

/** Fills a "{0}" placeholder, so the copy an editor writes decides where the number sits. */
const template = (text, value) => (text ?? "").replace("{0}", value);
